import React, { useState, useEffect } from 'react';
import { FileText, Save, Eye, Palette, Building2, FileCheck, Receipt, AlertCircle, Check } from 'lucide-react';
import api from '../lib/api';

const ConfiguracionDocumentos = () => {
  const [config, setConfig] = useState({
    company_name: 'SERAMA',
    company_cif: 'B12345678',
    company_address: 'Barcelona, España',
    company_phone: '+34 600 000 000',
    company_email: 'info@serama.es',
    primary_color: '#1E3A5F',
    accent_color: '#2563EB',
    show_logo: false,
    logo_url: '',
    legal_text: 'Los trabajos se realizarán conforme a las condiciones indicadas. Presupuesto válido según plazo indicado. Precios sin IVA salvo indicación contraria.',
    payment_info: 'Forma de pago: Transferencia bancaria\nIBAN: ES00 0000 0000 0000 0000 0000\nTitular: SERAMA Reformas S.L.',
    show_iva: true,
    show_irpf: false,
    irpf_percent: 15,
    iva_percent: 21
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('empresa');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/document-config');
      setConfig(response.data);
    } catch (err) {
      console.log('Using default config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.put('/document-config', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const colorPresets = [
    { name: 'Azul Corporativo', primary: '#1E3A5F', accent: '#2563EB' },
    { name: 'Verde Profesional', primary: '#14532D', accent: '#22C55E' },
    { name: 'Gris Elegante', primary: '#1F2937', accent: '#6B7280' },
    { name: 'Azul Oscuro', primary: '#0F172A', accent: '#3B82F6' },
    { name: 'Marrón Clásico', primary: '#44403C', accent: '#A16207' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración de Documentos</h1>
          <p className="text-slate-600 mt-1">Personaliza el aspecto de tus facturas y presupuestos</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-config-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          ) : saved ? (
            <Check className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? 'Guardando...' : saved ? 'Guardado' : 'Guardar cambios'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {saved && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          Configuración guardada correctamente
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-8">
          {[
            { id: 'empresa', label: 'Datos Empresa', icon: Building2 },
            { id: 'apariencia', label: 'Apariencia', icon: Palette },
            { id: 'impuestos', label: 'Impuestos', icon: Receipt },
            { id: 'textos', label: 'Textos Legales', icon: FileCheck },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'empresa' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-slate-500" />
                Datos de la Empresa
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nombre de la empresa
                  </label>
                  <input
                    type="text"
                    value={config.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    data-testid="input-company-name"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    CIF / NIF
                  </label>
                  <input
                    type="text"
                    value={config.company_cif}
                    onChange={(e) => handleChange('company_cif', e.target.value)}
                    data-testid="input-company-cif"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={config.company_address}
                    onChange={(e) => handleChange('company_address', e.target.value)}
                    data-testid="input-company-address"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="text"
                    value={config.company_phone}
                    onChange={(e) => handleChange('company_phone', e.target.value)}
                    data-testid="input-company-phone"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={config.company_email}
                    onChange={(e) => handleChange('company_email', e.target.value)}
                    data-testid="input-company-email"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'apariencia' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Palette className="h-5 w-5 text-slate-500" />
                Colores del Documento
              </h2>
              
              {/* Color Presets */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Paletas predefinidas
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorPresets.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => {
                        handleChange('primary_color', preset.primary);
                        handleChange('accent_color', preset.accent);
                      }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                    >
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: preset.primary }}
                      ></div>
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: preset.accent }}
                      ></div>
                      <span className="text-sm text-slate-600">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom Colors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Color Principal
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      data-testid="input-primary-color"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Color Acento
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.accent_color}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.accent_color}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      data-testid="input-accent-color"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Logo Option */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Mostrar Logo</label>
                    <p className="text-xs text-slate-500">Activa para mostrar el logo en documentos</p>
                  </div>
                  <button
                    onClick={() => handleChange('show_logo', !config.show_logo)}
                    data-testid="toggle-show-logo"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.show_logo ? 'bg-blue-600' : 'bg-slate-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.show_logo ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {config.show_logo && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      URL del Logo
                    </label>
                    <input
                      type="url"
                      value={config.logo_url}
                      onChange={(e) => handleChange('logo_url', e.target.value)}
                      placeholder="https://ejemplo.com/logo.png"
                      data-testid="input-logo-url"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'impuestos' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 text-slate-500" />
                Configuración de Impuestos
              </h2>
              
              {/* IVA */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700">Mostrar IVA</label>
                  <p className="text-xs text-slate-500">Incluir IVA en facturas y presupuestos</p>
                </div>
                <button
                  onClick={() => handleChange('show_iva', !config.show_iva)}
                  data-testid="toggle-show-iva"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.show_iva ? 'bg-blue-600' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.show_iva ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {config.show_iva && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Porcentaje de IVA (%)
                  </label>
                  <input
                    type="number"
                    value={config.iva_percent}
                    onChange={(e) => handleChange('iva_percent', parseFloat(e.target.value) || 0)}
                    data-testid="input-iva-percent"
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* IRPF */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-slate-700">Mostrar IRPF</label>
                  <p className="text-xs text-slate-500">Aplicar retención de IRPF (autónomos)</p>
                </div>
                <button
                  onClick={() => handleChange('show_irpf', !config.show_irpf)}
                  data-testid="toggle-show-irpf"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.show_irpf ? 'bg-blue-600' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.show_irpf ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {config.show_irpf && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Porcentaje de IRPF (%)
                  </label>
                  <input
                    type="number"
                    value={config.irpf_percent}
                    onChange={(e) => handleChange('irpf_percent', parseFloat(e.target.value) || 0)}
                    data-testid="input-irpf-percent"
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'textos' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-slate-500" />
                Textos Legales y de Pago
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Texto Legal / Condiciones
                </label>
                <textarea
                  value={config.legal_text}
                  onChange={(e) => handleChange('legal_text', e.target.value)}
                  rows={4}
                  data-testid="input-legal-text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Texto que aparecerá al pie de los documentos..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Este texto aparecerá en el pie de facturas y presupuestos
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Información de Pago
                </label>
                <textarea
                  value={config.payment_info}
                  onChange={(e) => handleChange('payment_info', e.target.value)}
                  rows={4}
                  data-testid="input-payment-info"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Datos bancarios, formas de pago aceptadas..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Información de pago que aparecerá en las facturas
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
                <Eye className="h-4 w-4 text-slate-500" />
                Vista Previa del Documento
              </h3>
              
              {/* Professional Preview - A4 simulation */}
              <div 
                className="bg-white border border-slate-300 rounded shadow-md overflow-hidden"
                style={{ 
                  aspectRatio: '210/297',
                  fontSize: '6px',
                  lineHeight: '1.3'
                }}
              >
                <div className="h-full flex flex-col p-3">
                  {/* Header */}
                  <div className="flex justify-between items-start pb-2 border-b border-slate-200">
                    <div className="flex-1">
                      {config.show_logo && config.logo_url ? (
                        <div className="w-8 h-8 bg-slate-100 rounded mb-1 flex items-center justify-center overflow-hidden">
                          <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'} />
                        </div>
                      ) : null}
                      <div className="font-bold" style={{ color: config.primary_color, fontSize: '10px' }}>
                        {config.company_name}
                      </div>
                      <div className="text-slate-500 mt-0.5" style={{ fontSize: '5px' }}>
                        {config.company_cif}<br/>
                        {config.company_address}<br/>
                        Tel: {config.company_phone}
                      </div>
                    </div>
                    <div className="text-right">
                      <div 
                        className="px-2 py-1 text-white rounded-sm"
                        style={{ backgroundColor: config.primary_color, fontSize: '7px' }}
                      >
                        <div className="font-bold">FACTURA</div>
                      </div>
                      <div 
                        className="px-2 py-0.5 mt-0.5 rounded-sm font-bold"
                        style={{ backgroundColor: '#F3F4F6', color: config.primary_color, fontSize: '8px' }}
                      >
                        FAC-2025-0001
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex gap-2 py-1.5 px-1.5 bg-slate-50 rounded-sm mt-2" style={{ fontSize: '5px' }}>
                    <span><b>Emisión:</b> 14/01/2025</span>
                    <span><b>Vencimiento:</b> 14/02/2025</span>
                  </div>

                  {/* Client */}
                  <div className="mt-2">
                    <div className="font-bold mb-0.5" style={{ color: config.primary_color, fontSize: '6px' }}>
                      CLIENTE
                    </div>
                    <div className="bg-slate-50 p-1.5 rounded-sm" style={{ fontSize: '5px' }}>
                      <b>María García López</b><br/>
                      Calle Mayor 45, Barcelona<br/>
                      Tel: 600 123 456
                    </div>
                  </div>

                  {/* Table */}
                  <div className="mt-2 flex-1">
                    <div className="font-bold mb-0.5" style={{ color: config.primary_color, fontSize: '6px' }}>
                      DETALLE
                    </div>
                    <div className="border border-slate-200 rounded-sm overflow-hidden">
                      <div 
                        className="grid grid-cols-12 gap-0.5 px-1 py-0.5 text-white"
                        style={{ backgroundColor: config.primary_color, fontSize: '5px' }}
                      >
                        <div className="col-span-6 font-bold">Descripción</div>
                        <div className="col-span-2 text-center font-bold">Cant.</div>
                        <div className="col-span-2 text-right font-bold">Precio</div>
                        <div className="col-span-2 text-right font-bold">Total</div>
                      </div>
                      <div className="grid grid-cols-12 gap-0.5 px-1 py-0.5 bg-white" style={{ fontSize: '5px' }}>
                        <div className="col-span-6">Reforma integral baño</div>
                        <div className="col-span-2 text-center">1</div>
                        <div className="col-span-2 text-right">2.500,00 €</div>
                        <div className="col-span-2 text-right font-medium">2.500,00 €</div>
                      </div>
                      <div className="grid grid-cols-12 gap-0.5 px-1 py-0.5 bg-slate-50" style={{ fontSize: '5px' }}>
                        <div className="col-span-6">Instalación eléctrica</div>
                        <div className="col-span-2 text-center">1</div>
                        <div className="col-span-2 text-right">890,00 €</div>
                        <div className="col-span-2 text-right font-medium">890,00 €</div>
                      </div>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="mt-auto pt-2">
                    <div className="text-right space-y-0.5 mb-1" style={{ fontSize: '5px' }}>
                      <div><b>Base Imponible:</b> 3.390,00 €</div>
                      {config.show_iva && (
                        <div><b>IVA ({config.iva_percent}%):</b> {(3390 * config.iva_percent / 100).toFixed(2).replace('.', ',')} €</div>
                      )}
                      {config.show_irpf && (
                        <div><b>IRPF (-{config.irpf_percent}%):</b> -{(3390 * config.irpf_percent / 100).toFixed(2).replace('.', ',')} €</div>
                      )}
                    </div>
                    <div 
                      className="flex justify-between items-center px-2 py-1 rounded-sm text-white"
                      style={{ backgroundColor: config.primary_color }}
                    >
                      <span className="font-bold" style={{ fontSize: '6px' }}>TOTAL A PAGAR</span>
                      <span className="font-bold" style={{ fontSize: '8px' }}>
                        {(3390 * (1 + (config.show_iva ? config.iva_percent : 0) / 100) - (config.show_irpf ? 3390 * config.irpf_percent / 100 : 0)).toFixed(2).replace('.', ',')} €
                      </span>
                    </div>

                    {/* Payment Info */}
                    <div 
                      className="mt-1.5 p-1.5 rounded-sm"
                      style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B', fontSize: '4.5px' }}
                    >
                      <div className="font-bold mb-0.5" style={{ color: '#92400E' }}>INFORMACIÓN DE PAGO</div>
                      <div className="text-slate-700 whitespace-pre-line" style={{ fontSize: '4px' }}>
                        {config.payment_info.substring(0, 80)}...
                      </div>
                    </div>

                    {/* Legal */}
                    <div className="mt-1.5 pt-1 border-t border-slate-200 text-slate-400" style={{ fontSize: '4px' }}>
                      {config.legal_text.substring(0, 100)}...
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 text-center mt-2">
                Simulación del documento final
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracionDocumentos;
